import { IMAGES, TESTS } from "@/data/content";
import { Gem, FlaskConical, Droplets, Scale, Magnet, Wind, Filter, GlassWater } from "lucide-react";

const TEST_ICONS = { Gem, FlaskConical, Droplets, Scale, Magnet, Wind };

const scrollToContact = (e) => {
  e.preventDefault();
  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
};

export function WhatWeTest() {
  return (
    <section id="test" className="py-20 bg-[#f8fbff] border-b border-slate-200" data-testid="what-we-test-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Clear Data First</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            Don't guess. Test your water.
          </h2>
          <p className="mt-3 text-slate-600">
            We test right at your kitchen sink while you watch. Depending on your water source, testing may
            evaluate the characteristics below — you see the readings, not a sales pitch.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTS.map((t, i) => {
            const Icon = TEST_ICONS[t.icon];
            return (
            <div key={t.title} className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-sm" data-testid={`test-card-${i}`}>
              <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mt-4">{t.title}</h3>
              <p className="text-sm text-slate-600 mt-2">{t.text}</p>
            </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a href="#contact" onClick={scrollToContact} data-testid="test-section-cta"
            className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-semibold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition shadow-md">
            Schedule Your Free In-Home Test
          </a>
          <p className="text-xs text-slate-600 mt-2">
            Takes 30 to 45 minutes &bull; Conducted at your kitchen sink &bull; Results on the spot
          </p>
        </div>
      </div>
    </section>
  );
}

const DIFFERENCE_POINTS = [
  { title: "Understand First", text: "We explain every number on your test report in plain English before even mentioning equipment options." },
  { title: "Whole-Home Thinking", text: "We look at the water your family drinks, cooks with, bathes in, and runs through every appliance — not a generic one-size-fits-all box." },
  { title: "Right-Sized Solutions", text: "If a small drinking filter is all you need, that is exactly what we will recommend. Conditioning is based on your water's actual conditions, not a package." },
  { title: "Straightforward Guidance", text: "We explain the problem, explain the options, and let you make an informed decision. No 'sign today only' theatrics." },
  { title: "Peace of Mind", text: "You leave the process understanding what is happening with your water and what your options are — whether or not you ever buy anything." },
];

export function Difference() {
  return (
    <section className="py-20 bg-white border-b border-slate-200" data-testid="difference-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Our Core Philosophy</span>
            <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
              Don't buy a system until you know what your water needs
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
              Whitestone isn't asking homeowners to buy a water system on the spot. We help you understand
              your water chemistry so you can make a calm, confident decision for your home.
            </p>

            <div className="mt-8 space-y-4">
              {DIFFERENCE_POINTS.map((p, i) => (
                <div key={p.title} className="flex items-start gap-4" data-testid={`difference-point-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">✓</div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{p.title}</h3>
                    <p className="text-sm text-slate-600">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="bg-[#0a1b2a] rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden shadow-xl">
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-sky-800/40 mb-8">
                <img src={IMAGES.consult} alt="A Whitestone specialist reviewing water test results with a homeowner"
                  className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1b2a]/90 via-[#0a1b2a]/20 to-transparent" />
                <div className="absolute inset-x-5 bottom-5">
                  <span className="text-xs font-semibold tracking-wider text-sky-300 uppercase">Consultative Process</span>
                  <p className="text-sm text-slate-200 mt-1">Homeowner education first. Every recommendation is backed by on-site test readings.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <span className="text-xs font-semibold text-sky-300">Our Commitment</span>
                  <p className="text-base font-medium text-slate-100 mt-1 italic">
                    "We treat your home water analysis like a diagnostic test, not a sales pitch."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SoftenerVsFilter() {
  return (
    <section id="softener-vs-filter" className="py-20 bg-[#f4f9fd] border-b border-slate-200" data-testid="softener-vs-filter-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Clear Education</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            Do you need a water softener, a filter — or both?
          </h2>
          <p className="mt-3 text-slate-600">
            These two technologies do completely different jobs. Knowing the difference prevents paying for
            what you do not need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col justify-between" data-testid="softener-card">
            <div>
              <div className="inline-block px-3 py-1 bg-sky-100 text-sky-800 text-xs font-bold rounded-lg uppercase tracking-wider mb-4">Excess Mineral Control</div>
              <h3 className="text-2xl font-bold text-slate-900">Whole-Home Water Softeners</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                Softening primarily addresses hardness minerals. Ion-exchange media removes dissolved calcium
                and magnesium — the minerals behind scale, spots, and soap that won't lather.
              </p>
              <div className="mt-6 space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">What Softeners Solve:</h4>
                {[
                  "Crusty white scale on showerheads and faucets",
                  "Soap scum on glass doors and bathtubs",
                  "Water that leaves skin and hair feeling dry after showers",
                  "Scale stress on water heaters and dishwashers",
                ].map((li) => (
                  <div key={li} className="flex items-center text-sm text-slate-700 gap-2">
                    <span className="text-sky-600 font-bold">•</span> {li}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100 bg-slate-50 -mx-8 -mb-8 p-8 rounded-b-3xl">
              <span className="text-xs font-bold text-slate-500 uppercase">Important note:</span>
              <p className="text-xs text-slate-600 mt-1">A basic water softener does NOT remove chlorine, bad taste, or chemical disinfectants.</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col justify-between" data-testid="filter-card">
            <div>
              <div className="inline-block px-3 py-1 bg-teal-100 text-teal-800 text-xs font-bold rounded-lg uppercase tracking-wider mb-4">Chemical &amp; Taste Removal</div>
              <h3 className="text-2xl font-bold text-slate-900">Whole-Home Water Filtration</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                Filtration addresses specific unwanted substances depending on the technology — catalytic
                carbon for chlorine and chloramine, sediment stages for particles, and other media matched
                to your water.
              </p>
              <div className="mt-6 space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">What Filtration Solves:</h4>
                {[
                  "Chemical chlorine / chloramine odor and taste",
                  "Disinfectant vapor noticeable during hot showers",
                  "Rust, fine dirt, and sediment from aging water mains",
                  "Crisp, clean-tasting drinking water from every tap",
                ].map((li) => (
                  <div key={li} className="flex items-center text-sm text-slate-700 gap-2">
                    <span className="text-teal-600 font-bold">•</span> {li}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100 bg-slate-50 -mx-8 -mb-8 p-8 rounded-b-3xl">
              <span className="text-xs font-bold text-slate-500 uppercase">Important note:</span>
              <p className="text-xs text-slate-600 mt-1">A filtration unit alone does NOT remove hardness minerals like calcium and magnesium.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">
            Whole-home conditioning can combine both approaches when your water calls for it. The answer starts
            with testing your water — not choosing a product from a menu.
          </p>
          <a href="#contact" onClick={scrollToContact} data-testid="softener-section-cta"
            className="mt-5 inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-semibold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition shadow-md">
            Find Out What Your Home Needs
          </a>
        </div>
      </div>
    </section>
  );
}

export function Pricing() {
  return (
    <section id="pricing" className="py-20 bg-white border-b border-slate-200" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Transparent Monthly Investment</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            What does whole-home water conditioning cost?
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Better water doesn't have to mean a large upfront expense. At Whitestone, we believe you should
            know what an investment in better water looks like before you ever schedule an appointment.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 bg-[#f8fbff] border border-slate-200 rounded-3xl p-8 sm:p-10" data-testid="pricing-solution-card">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-700">A Complete Water Solution</span>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#0a1b2a] mt-2">From $119<span className="text-lg font-semibold text-slate-600">/month</span></h3>
            <p className="text-sm text-slate-600 mt-2">A typical Whitestone solution can combine:</p>

            <div className="mt-6 space-y-5">
              <div className="flex items-start gap-4" data-testid="pricing-include-0">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center shrink-0">
                  <Droplets className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">Whole-Home Water Conditioning</h4>
                  <p className="text-sm text-slate-600 mt-0.5">Helps address hardness and scale throughout the home.</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="pricing-include-1">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center shrink-0">
                  <Filter className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">Multi-Stage Water Filtration</h4>
                  <p className="text-sm text-slate-600 mt-0.5">Designed to address unwanted substances such as chlorine, chloramine and other water-quality concerns based on your home's water.</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="pricing-include-2">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center shrink-0">
                  <GlassWater className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">High-Purity Drinking Water</h4>
                  <p className="text-sm text-slate-600 mt-0.5">A dedicated drinking-water solution can provide an additional level of filtration at the kitchen sink.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <h4 className="text-base font-bold text-slate-900">Your Home. Your Water. Your Solution.</h4>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                There isn't a one-size-fits-all system — and there shouldn't be a one-size-fits-all price.
                Your home's water source, water quality, household size, water usage and conditioning needs all
                influence the right solution. We'll test your water, explain what we find and show you the
                options that make sense for your home.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 bg-sky-900 text-white rounded-3xl p-8 sm:p-10 flex flex-col justify-between shadow-lg" data-testid="pricing-monthly-card">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-sky-200">Typical Monthly Investment</span>
              <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                <span className="editorial-title text-5xl sm:text-6xl text-white" data-testid="pricing-monthly-figure">$119–$145</span>
                <span className="text-sky-200 text-lg font-medium">/month</span>
              </div>
              <p className="text-sm text-sky-100 mt-4 leading-relaxed">
                Most Whitestone homeowners can expect a monthly investment in this range, depending on the
                home's water needs, selected conditioning, approved credit, and financing terms.
              </p>
            </div>
            <div className="mt-8">
              <a href="#contact" onClick={scrollToContact} data-testid="pricing-section-cta"
                className="flex items-center justify-center w-full py-4 rounded-xl text-base font-bold text-slate-950 bg-sky-300 hover:bg-sky-200 transition shadow-lg text-center">
                GET MY FREE WATER ANALYSIS
              </a>
              <p className="text-xs text-sky-100 mt-4 leading-relaxed">
                Wondering what your water needs? Find out before you buy — schedule your free Whitestone
                water analysis and get a clear picture of your home's water and the options available to you.
              </p>
              <p className="text-[11px] text-sky-300/80 mt-3 leading-relaxed">
                Actual payment varies based on selected equipment, water-conditioning needs, approved credit and
                financing terms. Financing is subject to approval. Terms and conditions apply.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
